import {
    CollisionEventType,
    Component,
    Object3D,
    PhysXComponent,
    WonderlandEngine,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {NpcComponent} from './npc-component.js';
import {gameState} from '../state/GameState.js';
import {Player, PlayerStates} from '../state/Player.js';
import {enemiesWithinRange, EnemyComponent} from './enemy-component.js';
import {BurstMeshParticles} from './burst-mesh-particles.js';
import {quat, vec3} from 'gl-matrix';
import {FruitData, FruitTypes, PowerFruit, allPowerFruit} from './power-fruit.js';
import {ControlEvents} from './control-events.js';
import {EnemyStates} from '../state/Enemy.js';
import {PlayerAnimationController} from './player-animation-controller.js';
import {playOneShotSafe, Sounds} from '../utils/Audio.js';
import {
    delay,
    FruitPowerVfxManager,
    move,
    rotateLocal,
    uniformScale,
    VFXPlayer,
} from './fruit-power-vfx-manager.js';
import {ShopComponent} from './shop-component.js';
import {SelfDestruct} from './self-destruct.js';
import {AxisY} from '../utils/math.js';
import {messageConsole} from './message-controller.js';

const tempVec = vec3.create();
const tempRot = quat.create();

export let playerObject: Object3D | null = null;

export class PlayerComponent extends Component {
    static TypeName = 'player-component';

    static onRegister(engine: WonderlandEngine) {
        engine.registerComponent(SelfDestruct);
    }

    @property.float(3)
    attackReach = 3;

    @property.object({required: true})
    spawnPoint!: Object3D;

    @property.object({required: true})
    impactParticleObject!: Object3D;

    @property.object({required: true})
    powerParticleObject!: Object3D;

    @property.object({required: true})
    fruitPowerVfxManagerObj!: Object3D;

    private _physXComponent!: PhysXComponent;
    private _attackReachPhysX!: PhysXComponent;
    private _isTouching: boolean = false;
    private _particles!: BurstMeshParticles;
    private _powerParticles!: BurstMeshParticles;
    private _playerObject!: Object3D;
    private _currentFruitData: FruitData | null = null;

    async start() {
        const s = await this.engine.loadPrefab('Player.bin');
        const r = this.engine.scene.instantiate(s);

        this._playerObject = r.root.findByNameDirect('Player')![0];
        this._playerObject.parent = this.object;

        this._particles = this.impactParticleObject.getComponent(BurstMeshParticles)!;
        this._powerParticles = this.powerParticleObject.getComponent(BurstMeshParticles)!;

        playerObject = this._playerObject;

        this._playerObject
            .getComponent(PlayerAnimationController)!
            .setPlayerData(gameState.currentPlayer);

        gameState.currentPlayer.onHit.add((damage) => {
            this.object.getPositionWorld(tempVec);
            gameState.addAttackTextParticle(Math.ceil(damage), tempVec);
            gameState.cameraShake();
        });
        gameState.currentPlayer.onRespawn.add(() => {
            this.object.setPositionWorld(this.spawnPoint.getPositionWorld(tempVec));
        });

        this._attackReachPhysX = this.object.addComponent(PhysXComponent, {
            extents: [this.attackReach, 0, 0],
            kinematic: true,
            trigger: true,
            groupsMask: 0b10000000,
            blocksMask: 0b10000000,
        })!;

        this._physXComponent = this.object.getComponent(PhysXComponent)!;
        this._physXComponent.onCollision(this._onCollision);
        this._attackReachPhysX.onCollision(this._withinAttackReach);
        this._isTouching = false;

        this.fruitPowerVfxManager =
            this.fruitPowerVfxManagerObj.getComponent(FruitPowerVfxManager)!;
        this.vfx = this.fruitPowerVfxManager.play.bind(this.fruitPowerVfxManager);

        gameState.currentPlayer.componentReady();
    }

    private fruitPowerVfxManager!: FruitPowerVfxManager;
    private vfx!: VFXPlayer;

    onActivate(): void {
        ControlEvents.onAttackStart.add(this._onMouseDown);
        ControlEvents.onPowerStart.add(this._onPowerStart);
    }

    onDeactivate(): void {
        ControlEvents.onAttackStart.remove(this._onMouseDown);
        ControlEvents.onPowerStart.remove(this._onPowerStart);
    }

    update(dt: number) {}

    private _onMouseDown = () => {
        this._performAttack(false);
    };

    private _onPowerStart = () => {
        this._performAttack(true);
    };

    async giantPower(play: VFXPlayer) {
        const playerPos = this.object.getPositionWorld();
        const hoverPos = vec3.create();
        vec3.copy(hoverPos, playerPos);
        hoverPos[1] += 3;

        play(move(this.object, playerPos, hoverPos), 1);
        await play(uniformScale(this.object, 1, 2), 1);
        await delay(2);
        play(move(this.object, hoverPos, playerPos), 1);
        await play(uniformScale(this.object, 2, 1), 1);
    }

    async electricPower(play: VFXPlayer) {
        // cast power:
        // - this costs energy
        // - doesnt matter if it hits or not
        // - hits multiple enemies

        const castDistance = allPowerFruit[FruitTypes.Electric]!.distance;

        const hitCount = 10;
        let enemyPos = vec3.create();
        let lastPos = vec3.create();
        this.object.getPositionWorld(lastPos);
        lastPos[1] += 1;

        let targets = enemiesWithinRange(lastPos, castDistance);

        if (targets.length == 0) {
            // no enemies in range
            return;
        }
        const bolt = this.fruitPowerVfxManager.boltObject.clone();
        bolt.active = true;

        for (let i = 0; i < hitCount && targets.length; ++i) {
            const enemy = targets[i % targets.length];
            enemy.setAggroOnPlayer(this);
            enemy.object.getPositionWorld(enemyPos);
            enemyPos[1] += 1;

            await play(move(bolt, lastPos, enemyPos), 0.2);

            const enemyData = enemy.getData();
            const damage = gameState.currentPlayer.attack(
                enemyData,
                this._currentFruitData
            );

            gameState.cameraShake(0.001, 0.1);
            this._powerParticles.spawn(enemyPos);
            gameState.addAttackTextParticle(Math.ceil(damage), enemyPos);

            [enemyPos, lastPos] = [lastPos, enemyPos];

            vec3.copy(tempVec, lastPos);
            /* Set back to the floor for range check, since enemy origin
             * is on the floor */
            tempVec[1] -= 1;
            targets = enemiesWithinRange(tempVec, castDistance);
        }

        bolt.destroy();
    }

    async rockPower(play: VFXPlayer) {
        const spikePrefab = this.fruitPowerVfxManager.spikeObject;
        const castDistance = allPowerFruit[FruitTypes.Rock]!.distance;
        let enemyPos = vec3.create();

        let fwd = vec3.create();
        this.object.getForwardWorld(fwd);

        let lastPos = vec3.create();
        this.object.getPositionWorld(lastPos);
        this.object.getRotationWorld(tempRot);
        lastPos[1] -= 1;

        const targetPos = vec3.create();

        const spikes = [];

        const hitCount = 10;
        for (let i = 0; i < hitCount; ++i) {
            const spike = spikePrefab.clone();
            spikes.push(spike);
            spike.active = true;

            vec3.add(lastPos, lastPos, fwd);
            vec3.add(targetPos, lastPos, fwd);
            targetPos[1] += 0.5;

            const s = 1 + (hitCount - i) * 0.05;
            spike.scaleLocal([s, s, s]);
            spike.rotateLocal(tempRot);
            spike.rotateAxisAngleDegLocal(fwd, (2 * (i % 2) - 1) * Math.random() * 45);
            play(move(spike, lastPos, targetPos), 0.05);
            await delay(0.025);
            gameState.cameraShake(0.001, 0.025);

            const targets = enemiesWithinRange(targetPos, castDistance);
            targets.forEach((e) => {
                e.object.getPositionWorld(enemyPos);
                const damage = gameState.currentPlayer.attack(
                    e.getData(),
                    this._currentFruitData
                );
                e.setAggroOnPlayer(this);
                this._powerParticles.spawn(enemyPos);
                gameState.addAttackTextParticle(Math.ceil(damage), enemyPos);
            });
            await delay(0.025);
        }

        await delay(3);

        spikes.forEach((o) => o.destroy());
    }
    async firePower(play: VFXPlayer) {
        let lastPos = vec3.create();

        this.object.getPositionWorld(lastPos);
        lastPos[1] += 0.05;
        const castDistance = allPowerFruit[FruitTypes.Fire]!.distance;
        const targets = enemiesWithinRange(lastPos, castDistance);
        for (let e = targets.length - 1; e >= 0; --e) {
            const firePrefab = this.fruitPowerVfxManager.fireObject.clone();
            const enemy = targets[e];

            firePrefab.active = true;
            let enemyPos = vec3.create();
            enemy.object.getPositionWorld(enemyPos);
            firePrefab.getComponent(BurstMeshParticles)?.spawn(enemyPos);
            firePrefab.setPositionWorld(enemyPos);
            // make sure the clone kills itself after a moment
            firePrefab.addComponent(SelfDestruct, {timer: 1500});

            enemy.setAggroOnPlayer(this);
            const edata = enemy.getData();
            if (!edata.dead) {
                const damage = gameState.currentPlayer.attack(
                    edata,
                    this._currentFruitData
                );

                enemy.object.getPositionWorld(enemyPos);
                this._powerParticles.spawn(enemyPos);
                gameState.addAttackTextParticle(Math.ceil(damage), enemyPos);
                if (edata.dead) {
                    targets.splice(e, 1);
                }
            }
        }
    }
    async sandPower(play: VFXPlayer) {
        let lastPos = vec3.create();

        const sandBurst =
            this.fruitPowerVfxManager.sandParticle.getComponent(BurstMeshParticles);

        const particles = [];
        const range = allPowerFruit[FruitTypes.Sand]!.distance;

        const timePerIteration = 0.25;
        const iterations = 20;
        const duration = iterations * timePerIteration;
        for (let e = 0; e < iterations; ++e) {
            for (let p = 0; p < 10; ++p) {
                const sandPrefab = this.fruitPowerVfxManager.sandParticle.clone(
                    this.object
                );
                sandPrefab.active = true;
                particles.push(sandPrefab);
                const dist = (0.25 + 0.75 * Math.random()) * range;
                sandPrefab.setPositionLocal([
                    Math.sin(Math.random() * 2 * Math.PI) * dist,
                    0.5 + Math.random() * 4,
                    Math.cos(Math.random() * 2 * Math.PI) * dist,
                ]);

                play(
                    rotateLocal(sandPrefab, AxisY as vec3, 0, 360 * Math.random() * 30),
                    duration - e * timePerIteration
                );
            }

            this.object.getPositionWorld(lastPos);
            lastPos[1] += 0.05;
            const targets = enemiesWithinRange(lastPos, range);

            targets.forEach((enemy) => {
                let enemyPos = vec3.create();
                enemy.object.getPositionWorld(enemyPos);

                enemy.setAggroOnPlayer(this);
                const edata = enemy.getData();
                if (!edata.dead) {
                    const damage = gameState.currentPlayer.attack(
                        edata,
                        this._currentFruitData
                    );
                    sandBurst?.spawn(enemyPos);

                    enemy.object.getPositionWorld(enemyPos);
                    this._powerParticles.spawn(enemyPos);
                    gameState.addAttackTextParticle(Math.ceil(damage), enemyPos);
                    if (edata.dead) {
                        targets.splice(e, 1);
                    }
                }
            });

            await delay(timePerIteration);
        }

        particles.forEach((p) => p.destroy());
    }

    async darkPower(play: VFXPlayer) {
        const hitCount = 10;

        const abyss = this.fruitPowerVfxManager.abyssObject.clone();
        abyss.active = true;

        let lastPos = vec3.create();
        this.object.getPositionWorld(lastPos);
        lastPos[1] += 0.05;
        abyss.setPositionWorld(lastPos);

        gameState.cameraShake(0.01, 0.5);
        await play(uniformScale(abyss, 0, 3), 0.5);
        const castDistance = allPowerFruit[FruitTypes.Dark]!.distance;
        let enemyPos = vec3.create();
        for (let i = 0; i < hitCount; ++i) {
            await delay(0.5);

            const targets = enemiesWithinRange(lastPos, castDistance);
            for (let e = targets.length - 1; e >= 0; --e) {
                const enemy = targets[e];
                enemy.setAggroOnPlayer(this);
                const edata = enemy.getData();
                if (!edata.dead) {
                    const damage = gameState.currentPlayer.attack(
                        edata,
                        this._currentFruitData
                    );

                    enemy.object.getPositionWorld(enemyPos);
                    this._powerParticles.spawn(enemyPos);
                    gameState.addAttackTextParticle(Math.ceil(damage), enemyPos);
                }
                if (edata.dead) targets.splice(e, 1);
            }
        }
        await play(uniformScale(abyss, 3, 0), 0.5);

        abyss.destroy();
    }

    private _performAttack(withFruit: boolean) {
        if (
            gameState.currentPlayer.state === PlayerStates.Dead ||
            gameState.currentPlayer.state === PlayerStates.Dying
        ) {
            return;
        }

        gameState.currentPlayer.setState(
            withFruit ? PlayerStates.Power : PlayerStates.Attack
        );

        if (withFruit) {
            if (gameState.currentPlayer.powerFruit === null) {
                // can't do fruit attack with no fruit
                return;
            }

            const targets = enemiesWithinRange(
                gameState.currentPlayer.position!,
                gameState.currentPlayer.powerFruit!
            );

            if (targets.length === 0) {
                return;
            }
            const hasCast = gameState.currentPlayer.castFruitPower();
            if (!hasCast) {
                // casting failed.
                return;
            }

            switch (gameState.currentPlayer.powerFruit) {
                case FruitTypes.Fire:
                    this.firePower(this.vfx);
                    return;
                case FruitTypes.Dark:
                    this.darkPower(this.vfx);
                    return;
                case FruitTypes.Electric:
                    this.electricPower(this.vfx);
                    return;
                case FruitTypes.Rock:
                    this.rockPower(this.vfx);
                    return;
                case FruitTypes.Sand:
                    this.sandPower(this.vfx);
                    return;
                default:
                    break;
            }
        }

        if (this.enemiesWithinReach.length > 0) {
            // Shake when we hit at least 1 enemy that is not dead
            let actualHit = false;

            let enemiesToRemove: number[] = [];
            this.enemiesWithinReach.forEach((enemy, i) => {
                if (enemy.isDestroyed) {
                    enemiesToRemove.push(i);
                    return;
                }
                const enemyData = enemy.getData();

                if (enemyData.state !== EnemyStates.Dead) {
                    const damage = gameState.currentPlayer.attack(
                        enemyData,
                        withFruit ? this._currentFruitData : null
                    );

                    if (damage > 0) {
                        actualHit = true;

                        enemy.object.getPositionWorld(tempVec);
                        tempVec[1] += 1;

                        if (withFruit) {
                            this._powerParticles.spawn(tempVec);
                        } else {
                            this._particles.spawn(tempVec);
                        }

                        tempVec[1] += 0.5;
                        gameState.addAttackTextParticle(Math.ceil(damage), tempVec);

                        if (enemyData.dead) {
                            enemiesToRemove.push(i);
                        }
                    }
                }
            });

            if (actualHit) {
                gameState.cameraShake();
            }

            enemiesToRemove.forEach((i) => {
                this.enemiesWithinReach.splice(i, 1);
            });
        }
    }
    private _onCollision = (type: CollisionEventType, other: PhysXComponent) => {
        if (other.object.isDestroyed) return;

        if (type === CollisionEventType.Touch && !this._isTouching) {
            const npc = other.object.getComponent(NpcComponent);
            const fruit = other.object.getComponent(PowerFruit);
            const shop = other.object.getComponent(ShopComponent);
            if (npc) {
                npc.getData().assignQuest(gameState.currentPlayer, npc.getData().quests[0]);
                this._isTouching = true;
            }
            if (fruit) {
                this._currentFruitData = fruit.fruitData;
                playOneShotSafe(Sounds.PowerUp, {volume: 0.7});
                this._powerParticles.setColor(fruit.color);
                gameState.currentPlayer.addFruit(fruit.fruitType);
                messageConsole.addMessage('Picked up ' + other.object.name);
                other.object.destroy();
            }
            if (shop) {
                gameState.currentPlayer.enterShop();
                shop.enterShop();
                other.object.lookAt(this.object.getPositionWorld());
                this._isTouching = true;
            }
        }
        if (type === CollisionEventType.TouchLost && this._isTouching) {
            this._isTouching = false;
            if (gameState.currentPlayer.isInShop) {
                gameState.currentPlayer.exitShop();
            }
        }
    };

    enemiesWithinReach: EnemyComponent[] = [];

    private _withinAttackReach = (type: CollisionEventType, other: PhysXComponent) => {
        if (other.object.isDestroyed) return;

        if (type === CollisionEventType.TriggerTouch) {
            const enemy = other.object.getComponent(EnemyComponent);
            if (enemy) {
                // Add the enemy to the list of enemies within reach
                this.enemiesWithinReach.push(enemy);
            }
        }
        if (type === CollisionEventType.TriggerTouchLost) {
            const enemy = other.object.getComponent(EnemyComponent);
            if (enemy) {
                // Remove the enemy from the list of enemies within reach
                const index = this.enemiesWithinReach.indexOf(enemy);
                if (index > -1) {
                    this.enemiesWithinReach.splice(index, 1);
                }
            }
        }
    };

    getData() {
        return gameState.currentPlayer;
    }
}
